import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Video, Clock, Phone, Search, Heart, Home, Users, ShoppingBag, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";

const HERO_IMG = "https://images.unsplash.com/photo-1758686254007-a1aec378eca3?w=800&q=80";

export default function Hero() {
  const scrollToBooking = () => {
    const el = document.querySelector("#termin-buchen");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToLeistungen = () => {
    const el = document.querySelector("#leistungen");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative pt-12 pb-16 lg:pt-16 lg:pb-20 overflow-hidden"
      aria-label="Lehnen & Weinacht – Alltagsbegleitung und Pflegeberatung"
    >
      {/* Hintergrund: Foto der Hauptgeschäftsstelle, durch helle Fläche abgeschwächt (Variante 1) */}
      <div className="absolute inset-0" data-testid="hero-geschaeftsstelle">
        <img
          src="/geschaeftsstelle-grefrath-oedt.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-[center_60%]"
          fetchPriority="high"
          data-testid="hero-geschaeftsstelle-bild"
        />
        <div className="absolute inset-0 bg-white/80" />
        <div className="absolute inset-x-0 top-0 h-[460px] bg-gradient-to-b from-white/80 via-white/50 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#F8FAFC]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hauptüberschrift */}
        <div className="text-center mb-10 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 border border-cyan-100 rounded-full mb-6">
            <Shield className="h-4 w-4 text-cyan-700" />
            <span className="text-sm font-semibold text-cyan-800">Persönlich vor Ort & digital</span>
          </div>
          <h1 className="font-['Manrope'] text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5">
            Unterstützung für<br className="hidden sm:block" />
            <span className="text-cyan-700"> Pflegebedürftige & Angehörige</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed mb-8">
            Wir begleiten Sie im Alltag und beraten Sie zu allen Fragen rund um Pflege — vor Ort in Ihrer Region und digital in ganz NRW.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3">
            <Button onClick={scrollToBooking} className="h-14 px-7 rounded-full bg-cyan-700 hover:bg-cyan-800 text-white text-base font-semibold shadow-lg shadow-cyan-700/20" data-testid="hero-cta-termin">
              Jetzt Termin buchen
            </Button>
            <Button asChild variant="outline" className="h-14 px-7 rounded-full bg-white text-slate-900 text-base font-semibold border-slate-200 hover:bg-slate-50">
              <a href="tel:+491637917044" data-testid="hero-cta-telefon"><Phone className="h-4 w-4 mr-2" />0163 791 7044</a>
            </Button>
            <Button onClick={scrollToLeistungen} variant="outline" className="h-14 px-7 rounded-full bg-cyan-50 text-cyan-800 text-base font-semibold border-cyan-100 hover:bg-cyan-100" data-testid="hero-cta-leistungen">
              Leistungen prüfen
            </Button>
          </div>
          <p className="inline-block text-xs text-slate-600 mt-5 px-3 py-1 rounded-full bg-white/85" data-testid="hero-geschaeftsstelle-hinweis">
            Im Hintergrund: unsere Hauptgeschäftsstelle in Grefrath-Oedt – ein Standort von mehreren in der Region.
          </p>
        </div>

        {/* Zwei Bereiche */}
        <div className="grid md:grid-cols-2 gap-6 mb-12" data-testid="hero-dual-entry">
          {/* Alltagsbegleitung */}
          <Link
            to="/alltagsbegleitung"
            data-testid="hero-alltagsbegleitung"
            className="group bg-white rounded-2xl border-2 border-green-200/60 p-6 sm:p-8 hover:shadow-xl hover:border-green-300 hover:-translate-y-1 transition-all"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Heart className="h-6 w-6 text-green-700" />
              </div>
              <div>
                <span className="text-xs font-semibold text-green-700 bg-green-50 px-2.5 py-1 rounded-full">Vor Ort</span>
              </div>
            </div>
            <h2 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-3 group-hover:text-green-700 transition-colors">
              Alltagsbegleitung
            </h2>
            <ul className="space-y-2 mb-5">
              {["Unterstützung im Haushalt & Alltag", "Einkaufen & Begleitung zu Terminen", "Entlastung pflegender Angehöriger", "Anerkannte Angebote nach Landesrecht"].map((t, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                  <div className="h-1.5 w-1.5 bg-green-500 rounded-full shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
            <span className="inline-flex items-center gap-2 text-green-700 font-semibold group-hover:gap-3 transition-all">
              Alltagsbegleitung anfragen <ArrowRight className="h-4 w-4" />
            </span>
          </Link>

          {/* Pflegeberatung */}
          <div
            data-testid="hero-pflegeberatung"
            className="group bg-white rounded-2xl border-2 border-cyan-200/60 p-6 sm:p-8 hover:shadow-xl hover:border-cyan-300 hover:-translate-y-1 transition-all cursor-pointer"
            onClick={scrollToLeistungen}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 bg-cyan-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="h-6 w-6 text-cyan-700" />
              </div>
              <div>
                <span className="text-xs font-semibold text-cyan-700 bg-cyan-50 px-2.5 py-1 rounded-full">Digital & vor Ort</span>
              </div>
            </div>
            <h2 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-3 group-hover:text-cyan-700 transition-colors">
              Pflegeberatung
            </h2>
            <ul className="space-y-2 mb-5">
              {["Beratungsbesuch nach § 37 Abs. 3 SGB XI", "Digitale Pflegeberatung per Video/Telefon", "Hilfe bei Pflegegrad & Pflegeleistungen", "Online-Terminbuchung"].map((t, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                  <div className="h-1.5 w-1.5 bg-cyan-500 rounded-full shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
            <span className="inline-flex items-center gap-2 text-cyan-700 font-semibold group-hover:gap-3 transition-all">
              Pflegeberatung ansehen <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>

        {/* Quick stats */}
        <div className="flex flex-wrap justify-center gap-6 text-sm sm:text-base text-slate-500 mb-5">
          <div className="flex items-center gap-2">
            <Home className="h-4 w-4 text-green-600" />
            <span>Vor Ort in Ihrer Region</span>
          </div>
          <div className="flex items-center gap-2">
            <Video className="h-4 w-4 text-cyan-600" />
            <span>Video-Beratung</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-cyan-600" />
            <span>Termine innerhalb 48h</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-green-600" />
            <span>Kostenfrei mit Pflegegrad</span>
          </div>
        </div>

        {/* Finder link */}
        <div className="text-center">
          <Link
            to="/blog/pflegeleistungen-finder"
            data-testid="hero-finder-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-cyan-700 hover:text-cyan-600 hover:underline transition-all"
          >
            <Search className="h-4 w-4" />
            Welche Leistungen stehen Ihnen zu? Jetzt prüfen
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
